import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useReturnPath } from "hooks/useReturnPath"
import { useFormik } from "formik"
import * as Yup from "yup"
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap"
import PageWrapper from "components/Common/PageWrapper"
import {
  usePreOrder,
  useCreatePreOrder,
  useEditPreOrder,
} from "hooks/usePreOrder"
import {
  useCustomer,
  useCustomers,
  useCustomerSearchAPI,
} from "hooks/useCustomer"
import CustomerSearch from "pages/QuotationPages/Quotation/Customer/CustomerSearch"
import { useVehicleSearchQuery } from "hooks/stockHook"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import { useToastMessage } from "hooks/useToastMessage"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import {
  buildCreatableOptions,
  buildCustomerTypeOptions,
  buildCustomerSuggestionBuckets,
  formatCustomerTypeLabel,
  getCustomerDepartmentText,
  getCustomerOrganizationText,
  getCustomerTypeFieldMeta,
  resolveCustomerTypeKey,
} from "helpers/customerOnboarding"
import "pages/MoneyReceiptPages/MoneyReceiptAddEdit.css"

const extractCollection = value => {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.data)) return value.data
  return []
}

const getDisplayText = value => {
  if (!value) return ""
  if (typeof value === "string") return value
  if (typeof value === "object")
    return value?.name || value?.title || value?.label || ""
  return String(value)
}

const getVehiclePreviewLabel = vehicle =>
  [
    getDisplayText(vehicle?.brand),
    getDisplayText(vehicle?.model),
    getDisplayText(vehicle?.package),
    getDisplayText(vehicle?.engine_type),
  ]
    .filter(Boolean)
    .join(" ")

const buildCustomerPreviewRows = customer => {
  if (!customer) return []

  const customerType = resolveCustomerTypeKey(customer?.customer_type)
  const organizationLabel = customerType === "banker" ? "Bank" : "Organization"
  const departmentLabel = customerType === "banker" ? "Branch" : "Department"

  return [
    { label: "Type", value: formatCustomerTypeLabel(customerType) || "-" },
    { label: "Mobile", value: customer?.mobile || "-" },
    { label: "Email", value: customer?.email || "-" },
    {
      label: "Designation",
      value: getDisplayText(customer?.designation) || "-",
    },
    {
      label: organizationLabel,
      value: getCustomerOrganizationText(customer) || "-",
    },
    {
      label: departmentLabel,
      value: getCustomerDepartmentText(customer) || "-",
    },
    { label: "Address", value: customer?.full_address || "-" },
  ]
}

const formatAmount = value =>
  Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })

const getCustomerTypeKey = customer =>
  resolveCustomerTypeKey(customer?.customer_type)

const buildCustomerPdfContent = customer => ({
  name: customer?.name || "",
  mobile: customer?.mobile || "",
  email: customer?.email || "",
  customer_type: getCustomerTypeKey(customer) || "general",
  designation: getDisplayText(customer?.designation),
  company_name:
    getCustomerTypeKey(customer) === "service"
      ? customer?.company_name || ""
      : "",
  bank_name:
    getCustomerTypeKey(customer) === "banker" ? customer?.bank_name || "" : "",
  branch_name:
    getCustomerTypeKey(customer) === "banker"
      ? customer?.branch_name || ""
      : "",
  address: customer?.full_address || "",
})

const buildVehicleName = vehicle =>
  [
    getDisplayText(vehicle?.brand),
    getDisplayText(vehicle?.model),
    getDisplayText(vehicle?.package),
    getDisplayText(vehicle?.engine_type),
  ]
    .filter(Boolean)
    .join(" ")

const buildVehiclePdfContent = (vehicle, existingVehicleInfo = {}) => ({
  stock_id: vehicle?.stock_id || existingVehicleInfo?.stock_id || "",
  name: buildVehicleName(vehicle) || existingVehicleInfo?.name || "",
  full_chassis:
    vehicle?.full_chassis || existingVehicleInfo?.full_chassis || "",
  full_engine: vehicle?.full_engine || existingVehicleInfo?.full_engine || "",
  year_model:
    getDisplayText(vehicle?.year) || existingVehicleInfo?.year_model || "",
  color:
    getDisplayText(vehicle?.exterior_color) || existingVehicleInfo?.color || "",
  cc: getDisplayText(vehicle?.engine_c_c) || existingVehicleInfo?.cc || "",
  fuel: getDisplayText(vehicle?.fuel_type) || existingVehicleInfo?.fuel || "",
  brta_reg: vehicle?.brta_reg_no || existingVehicleInfo?.brta_reg || "",
})

const PreOrderAddEditPage = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const { fromPath } = useReturnPath("/customer-list")
  const isEdit = Boolean(id)
  const prefillCuid = searchParams.get("cuid")
  // Customer came in prefilled from the customer profile — keep it locked.
  const customerLocked = !!prefillCuid

  const { handleResponse, handleError } = useToastMessage()

  const { data: preOrderResponse, isLoading } = usePreOrder(id, isEdit)
  const { mutateAsync: createPreOrder, isPending: creating } =
    useCreatePreOrder()
  const { mutateAsync: editPreOrder, isPending: editing } =
    useEditPreOrder()
  const { data: prefillCustomerResponse, isLoading: prefillCustomerLoading } =
    useCustomer(prefillCuid, !isEdit && !!prefillCuid)
  const { customerSearchAPI, loading: customerSearching } =
    useCustomerSearchAPI({ limit: 8 })
  const { data: customerResponse } = useCustomers({ all: true, per_page: 200 })
  const { raw: filterOptionData, isLoading: customerLookupLoading } =
    useFilteredVehicleOptions()

  const [selectedCustomerItem, setSelectedCustomerItem] = useState(null)
  const [selectedVehicleItem, setSelectedVehicleItem] = useState(null)
  const [vehicleSearchTerm, setVehicleSearchTerm] = useState("")
  const [showVehicleSuggestions, setShowVehicleSuggestions] = useState(false)
  const vehicleSearchRef = useRef(null)
  const { data: vehicleSearchResults, isLoading: vehicleSearching } =
    useVehicleSearchQuery(vehicleSearchTerm, { limit: 8 })
  const cuidPrefilledRef = useRef(false)
  const lastEditPrefilledIdRef = useRef(null)

  const preOrder = preOrderResponse?.data ?? {}
  const isSaving = creating || editing

  const customers = extractCollection(customerResponse?.data)
  const customerLookupData = useMemo(() => {
    const suggestionBuckets = buildCustomerSuggestionBuckets(customers)
    const rawCustomerLookupData = filterOptionData?.customer ?? {}

    return {
      customer_types:
        rawCustomerLookupData?.customer_types ??
        suggestionBuckets.customerTypes ??
        [],
      designations:
        rawCustomerLookupData?.designations ??
        suggestionBuckets.designations ??
        [],
      companies:
        rawCustomerLookupData?.companies ?? suggestionBuckets.companies ?? [],
      banks: rawCustomerLookupData?.banks ?? suggestionBuckets.banks ?? [],
      branches:
        rawCustomerLookupData?.branches ?? suggestionBuckets.branches ?? [],
    }
  }, [customers, filterOptionData?.customer])

  const customerTypeSelectOptions = useMemo(
    () => buildCustomerTypeOptions(customerLookupData?.customer_types ?? []),
    [customerLookupData?.customer_types]
  )
  const designationSelectOptions = useMemo(
    () => buildCreatableOptions(customerLookupData?.designations ?? []),
    [customerLookupData?.designations]
  )
  const companySelectOptions = useMemo(
    () => buildCreatableOptions(customerLookupData?.companies ?? []),
    [customerLookupData?.companies]
  )
  const bankSelectOptions = useMemo(
    () => buildCreatableOptions(customerLookupData?.banks ?? []),
    [customerLookupData?.banks]
  )
  const branchSelectOptions = useMemo(
    () => buildCreatableOptions(customerLookupData?.branches ?? []),
    [customerLookupData?.branches]
  )

  const initialValues = useMemo(
    () => ({
      customer_id: preOrder?.customer_id ?? "",
      vehicle_id: preOrder?.vehicle_id ?? "",
      car_price: preOrder?.car_price ?? "",
      registration_cost: preOrder?.registration_cost ?? 0,
      vehicle_description: preOrder?.vehicle_description ?? "",
      notes: preOrder?.notes ?? "",
      pdf_content: {
        customer_info:
          preOrder?.pdf_content?.customer_info ??
          buildCustomerPdfContent(preOrder?.customer),
        vehicle_info: buildVehiclePdfContent(
          null,
          preOrder?.pdf_content?.vehicle_info ??
            buildVehiclePdfContent(preOrder?.vehicle)
        ),
      },
    }),
    [preOrder]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      customer_id: Yup.mixed().test(
        "customer-id-or-contact",
        "Select a customer or provide customer name.",
        function (value) {
          const customerInfo = this.parent?.pdf_content?.customer_info || {}
          return Boolean(value) || Boolean(customerInfo?.name?.trim())
        }
      ),
      car_price: Yup.number()
        .typeError("Car price must be numeric")
        .min(0, "Car price cannot be negative")
        .nullable(),
      registration_cost: Yup.number()
        .typeError("Registration cost must be numeric")
        .min(0, "Registration cost cannot be negative")
        .nullable(),
      vehicle_description: Yup.string()
        .nullable()
        .max(500, "Description is too long"),
      notes: Yup.string().nullable().max(1000, "Notes are too long"),
    }),
    onSubmit: async values => {
      const vehicleInfo = values.pdf_content?.vehicle_info || {}
      const fullChassis = vehicleInfo.full_chassis?.trim() || ""
      const fullEngine = vehicleInfo.full_engine?.trim() || ""

      const payload = {
        customer_id: values.customer_id || null,
        vehicle_id: values.vehicle_id || null,
        car_price: values.car_price === "" ? null : Number(values.car_price),
        registration_cost: Number(values.registration_cost || 0),
        ...(fullChassis || fullEngine
          ? {
              new_vehicle: {
                ...(fullChassis ? { full_chassis: fullChassis } : {}),
                ...(fullEngine ? { full_engine: fullEngine } : {}),
                ...(vehicleInfo.year_model?.trim()
                  ? { year_model: vehicleInfo.year_model.trim() }
                  : {}),
                ...(vehicleInfo.color?.trim()
                  ? { color: vehicleInfo.color.trim() }
                  : {}),
                ...(vehicleInfo.cc?.trim() ? { cc: vehicleInfo.cc.trim() } : {}),
                ...(vehicleInfo.fuel?.trim()
                  ? { fuel: vehicleInfo.fuel.trim() }
                  : {}),
              },
            }
          : {}),
        vehicle_description: values.vehicle_description?.trim() || null,
        pdf_content: {
          customer_info: values.pdf_content?.customer_info || {},
          vehicle_info: vehicleInfo,
        },
        notes: values.notes?.trim() || null,
      }

      try {
        const response = isEdit
          ? await editPreOrder({ id, ...payload })
          : await createPreOrder(payload)

        handleResponse(
          response,
          `Pre-order ${isEdit ? "updated" : "created"} successfully.`,
          true
        )

        const savedId = response?.data?.id ?? id
        navigate(savedId ? `/pre-orders/${savedId}` : fromPath)
      } catch (error) {
        handleError(error, "Failed to save pre-order.")
      }
    },
  })

  const refillCustomerPdfContent = customer =>
    formik.setFieldValue(
      "pdf_content.customer_info",
      buildCustomerPdfContent(customer)
    )

  const handleCustomerSelect = customer => {
    setSelectedCustomerItem(customer)
    formik.setFieldValue("customer_id", customer?.id || "")
    refillCustomerPdfContent(customer)
  }

  const clearCustomerSelection = () => {
    setSelectedCustomerItem(null)
    formik.setFieldValue("customer_id", "")
    refillCustomerPdfContent(null)
  }

  const handleCustomerTypeChange = value => {
    const nextTypeKey = resolveCustomerTypeKey(value)

    formik.setFieldValue("pdf_content.customer_info.customer_type", value)

    if (nextTypeKey === "general") {
      formik.setFieldValue("pdf_content.customer_info.designation", "")
      formik.setFieldValue("pdf_content.customer_info.company_name", "")
      formik.setFieldValue("pdf_content.customer_info.bank_name", "")
      formik.setFieldValue("pdf_content.customer_info.branch_name", "")
      return
    }

    if (nextTypeKey === "service") {
      formik.setFieldValue("pdf_content.customer_info.bank_name", "")
      formik.setFieldValue("pdf_content.customer_info.branch_name", "")
      return
    }

    formik.setFieldValue("pdf_content.customer_info.company_name", "")
  }

  const refillVehiclePdfContent = vehicle =>
    formik.setFieldValue(
      "pdf_content.vehicle_info",
      buildVehiclePdfContent(vehicle, formik.values.pdf_content?.vehicle_info)
    )

  const handleVehicleSelect = vehicle => {
    setSelectedVehicleItem(vehicle)
    formik.setFieldValue("vehicle_id", vehicle?.id || "")
    formik.setFieldValue(
      "pdf_content.vehicle_info",
      buildVehiclePdfContent(vehicle)
    )
    setVehicleSearchTerm("")
    setShowVehicleSuggestions(false)
  }

  const clearVehicleSelection = () => {
    setSelectedVehicleItem(null)
    formik.setFieldValue("vehicle_id", "")
    formik.setFieldValue("pdf_content.vehicle_info", buildVehiclePdfContent(null))
  }

  useEffect(() => {
    if (isEdit) return
    if (cuidPrefilledRef.current) return
    const customer = prefillCustomerResponse?.data
    if (!customer?.id) return

    setSelectedCustomerItem(customer)
    formik.setFieldValue("customer_id", customer.id)
    refillCustomerPdfContent(customer)
    cuidPrefilledRef.current = true
  }, [isEdit, prefillCustomerResponse?.data])

  useEffect(() => {
    if (!isEdit || !preOrder?.id) return
    if (lastEditPrefilledIdRef.current === preOrder.id) return

    setSelectedCustomerItem(preOrder?.customer ?? null)
    setSelectedVehicleItem(preOrder?.vehicle ?? null)
    lastEditPrefilledIdRef.current = preOrder.id
  }, [isEdit, preOrder?.id, preOrder?.customer, preOrder?.vehicle])

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        vehicleSearchRef.current &&
        !vehicleSearchRef.current.contains(event.target)
      ) {
        setShowVehicleSuggestions(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const selectedCustomerProfileRows = buildCustomerPreviewRows(
    selectedCustomerItem
  )
  const selectedCustomerType = resolveCustomerTypeKey(
    formik.values.pdf_content?.customer_info?.customer_type ||
      selectedCustomerItem?.customer_type
  )
  const selectedCustomerTypeMeta =
    getCustomerTypeFieldMeta(selectedCustomerType)
  const isServiceCustomer = selectedCustomerType === "service"
  const isBankerCustomer = selectedCustomerType === "banker"
  const customerInfoValues = formik.values.pdf_content?.customer_info || {}
  const vehicleInfoValues = formik.values.pdf_content?.vehicle_info || {}
  const hasVehicleTarget =
    Boolean(formik.values.vehicle_id) ||
    Boolean(vehicleInfoValues.full_chassis?.trim()) ||
    Boolean(vehicleInfoValues.full_engine?.trim())
  const amountPaid = Number(preOrder?.deposit_amount || 0)
  const totalAmount =
    Number(formik.values.car_price || 0) +
    Number(formik.values.registration_cost || 0)
  const overpaidAmount = Math.max(0, amountPaid - totalAmount)
  const dueAmount = Math.max(0, totalAmount - amountPaid)
  const summaryInfo = [
    { label: "Car Price", value: Number(formik.values.car_price || 0) },
    {
      label: "Registration Cost",
      value: Number(formik.values.registration_cost || 0),
    },
    { label: "Total Amount", value: totalAmount, strong: true },
    { label: "Deposit Paid", value: amountPaid },
    { label: "Overpaid", value: overpaidAmount },
    { label: "Due", value: dueAmount, strong: dueAmount > 0 },
  ].filter(item => item.value > 0 || item.label === "Total Amount")

  return (
    <PageWrapper
      title="Pre-Order"
      breadcrumbItem={isEdit ? "Edit Pre-Order" : "Create Pre-Order"}
    >
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0">
            {isEdit ? "Edit Pre-Order" : "Create Pre-Order"}
          </h4>
          <div className="text-muted">
            Reserve a vehicle for a customer with a booking deposit.
          </div>
        </div>
        <Button color="light" onClick={() => navigate(fromPath)}>
          Back
        </Button>
      </div>

      <Card className="border-0 shadow-none mb-0">
        <CardBody className="px-0 px-sm-2 px-md-4">
          {isLoading && isEdit ? (
            <div>Loading...</div>
          ) : (
            <Form onSubmit={formik.handleSubmit}>
              <div className="a4-container">
                <div className="a4-paper">
                  <div className="mr-header">
                    <Row className="align-items-start g-3">
                      <Col md={7}>
                        <div className="mr-brand">AUROBIL CARS</div>
                        <div className="text-muted small">
                          Pre-order entry form
                        </div>
                        <div className="small mt-2">
                          Entering a chassis or engine number will match or
                          create the vehicle in stock and mark it as Reserved.
                        </div>
                      </Col>
                      <Col md={5}>
                        <div className="receipt-info">
                          <div className="d-flex justify-content-between">
                            <span className="fw-semibold">Booking No.</span>
                            <span>{preOrder?.poid || "Auto-generated"}</span>
                          </div>
                          <div className="d-flex justify-content-between mt-2">
                            <span className="fw-semibold">Status</span>
                            <span>
                              {preOrder?.status === "cancelled"
                                ? "Cancelled"
                                : "Booked"}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between mt-2">
                            <span className="fw-semibold">Deposit</span>
                            <span>{formatAmount(preOrder?.deposit_amount)}</span>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>

                  <Row className="g-3">
                    <Col md={12}>
                      <div className="section-header">
                        <h6>CUSTOMER INFORMATION</h6>
                        <hr />
                      </div>
                    </Col>

                    <Col md={12}>
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                        <Label className="form-label mb-0">Customer</Label>
                        {selectedCustomerItem && (
                          <div className="d-flex gap-2 flex-wrap">
                            {!customerLocked && !isEdit && (
                              <Button
                                color="outline-danger"
                                size="sm"
                                type="button"
                                onClick={clearCustomerSelection}
                              >
                                <i className="bx bx-x me-1"></i>
                                Clear
                              </Button>
                            )}
                            <Button
                              color="outline-success"
                              size="sm"
                              type="button"
                              onClick={() =>
                                refillCustomerPdfContent(selectedCustomerItem)
                              }
                            >
                              <i className="bx bx-refresh me-1"></i>
                              Refill
                            </Button>
                          </div>
                        )}
                      </div>

                      {!selectedCustomerItem && customerLocked && (
                        <div className="text-muted small">
                          {prefillCustomerLoading
                            ? "Loading customer..."
                            : "Customer not found."}
                        </div>
                      )}

                      {!selectedCustomerItem && !customerLocked && !isEdit && (
                        <CustomerSearch
                          onCustomerSelect={handleCustomerSelect}
                          apiSearchFunction={customerSearchAPI}
                          placeholder="Search customers by name, email, phone..."
                          isSearching={customerSearching}
                        />
                      )}

                      {formik.touched.customer_id &&
                        formik.errors.customer_id && (
                          <div className="text-danger small mt-1">
                            {formik.errors.customer_id}
                          </div>
                        )}
                    </Col>

                    {selectedCustomerItem && (
                      <Col md={12}>
                        <div className="selection-preview-card customer-preview-card">
                          <div className="selection-preview-icon">
                            <i className="bx bx-user"></i>
                          </div>
                          <div className="w-100">
                            <h6 className="mb-1">Customer Details</h6>
                            <div className="text-muted small mb-2">
                              ID:{" "}
                              {selectedCustomerItem?.cuid ||
                                selectedCustomerItem?.id ||
                                "-"}
                              {"  "}Name: {selectedCustomerItem?.name || "-"}
                            </div>
                            <Row className="g-2 small">
                              {selectedCustomerProfileRows.map(row => (
                                <Col xs={12} sm={6} key={row.label}>
                                  <span className="text-muted">
                                    {row.label}:
                                  </span>{" "}
                                  {row.value}
                                </Col>
                              ))}
                            </Row>
                          </div>
                        </div>
                      </Col>
                    )}

                    <Col md={12}>
                      <div className="editor-panel">
                        <Row className="g-3">
                          <Col xs={12} sm={6}>
                            <Label className="form-label">Name</Label>
                            <Input
                              name="pdf_content.customer_info.name"
                              value={customerInfoValues.name || ""}
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col xs={12} sm={6}>
                            <Label className="form-label">Mobile</Label>
                            <Input
                              name="pdf_content.customer_info.mobile"
                              value={customerInfoValues.mobile || ""}
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col xs={12} sm={6}>
                            <Label className="form-label">Email</Label>
                            <Input
                              name="pdf_content.customer_info.email"
                              value={customerInfoValues.email || ""}
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col xs={12} sm={6}>
                            <FormikCreatableSelect
                              name="pdf_content.customer_info.customer_type"
                              label="Customer Type"
                              formik={formik}
                              options={customerTypeSelectOptions}
                              placeholder="Select or type customer type"
                              loading={customerLookupLoading}
                              onChange={handleCustomerTypeChange}
                            />
                          </Col>
                          {(isServiceCustomer || isBankerCustomer) && (
                            <Col xs={12} sm={6} lg={4}>
                              <FormikCreatableSelect
                                name="pdf_content.customer_info.designation"
                                label="Designation"
                                formik={formik}
                                options={designationSelectOptions}
                                placeholder="Select or type a designation"
                                loading={customerLookupLoading}
                              />
                            </Col>
                          )}
                          {(isServiceCustomer || isBankerCustomer) && (
                            <Col xs={12} sm={6} lg={4}>
                              <FormikCreatableSelect
                                name={
                                  isBankerCustomer
                                    ? "pdf_content.customer_info.bank_name"
                                    : "pdf_content.customer_info.company_name"
                                }
                                label={
                                  selectedCustomerTypeMeta.organizationLabel
                                }
                                formik={formik}
                                options={
                                  isBankerCustomer
                                    ? bankSelectOptions
                                    : companySelectOptions
                                }
                                placeholder={`Select or type ${selectedCustomerTypeMeta.organizationLabel.toLowerCase()}`}
                                loading={customerLookupLoading}
                              />
                            </Col>
                          )}
                          {isBankerCustomer && (
                            <Col xs={12} sm={6} lg={4}>
                              <FormikCreatableSelect
                                name="pdf_content.customer_info.branch_name"
                                label="Branch Name"
                                formik={formik}
                                options={branchSelectOptions}
                                placeholder="Select or type branch name"
                                loading={customerLookupLoading}
                              />
                            </Col>
                          )}
                          <Col md={12}>
                            <Label className="form-label">Address</Label>
                            <Input
                              type="textarea"
                              rows={3}
                              name="pdf_content.customer_info.address"
                              value={customerInfoValues.address || ""}
                              onChange={formik.handleChange}
                              placeholder="Customer address shown in the PDF"
                            />
                          </Col>
                        </Row>
                      </div>
                    </Col>

                    <Col md={12}>
                      <div className="section-header">
                        <h6>VEHICLE INFORMATION</h6>
                        <hr />
                      </div>
                    </Col>

                    <Col md={12}>
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                        <Label className="form-label mb-0">Vehicle</Label>
                        {selectedVehicleItem && (
                          <div className="d-flex gap-2 flex-wrap">
                            <Button
                              color="outline-danger"
                              size="sm"
                              type="button"
                              onClick={clearVehicleSelection}
                            >
                              <i className="bx bx-x me-1"></i>
                              Clear
                            </Button>
                            <Button
                              color="outline-success"
                              size="sm"
                              type="button"
                              onClick={() =>
                                refillVehiclePdfContent(selectedVehicleItem)
                              }
                            >
                              <i className="bx bx-refresh me-1"></i>
                              Refill
                            </Button>
                          </div>
                        )}
                      </div>

                      {!selectedVehicleItem && (
                        <div
                          className="vehicle-selector position-relative"
                          ref={vehicleSearchRef}
                        >
                          <Input
                            type="text"
                            placeholder="Search existing stock by vehicle name, chassis no, stock id, or engine no... (optional)"
                            value={vehicleSearchTerm}
                            onChange={e => {
                              setVehicleSearchTerm(e.target.value)
                              setShowVehicleSuggestions(true)
                            }}
                            onFocus={() => setShowVehicleSuggestions(true)}
                          />
                          {vehicleSearching && (
                            <small className="text-muted d-flex align-items-center gap-1 mt-1">
                              <Spinner size="sm" type="grow" />
                              Searching vehicles...
                            </small>
                          )}

                          {showVehicleSuggestions &&
                            vehicleSearchResults?.length > 0 && (
                              <Card
                                className="position-absolute w-100 mt-1 shadow"
                                style={{ zIndex: 1000 }}
                              >
                                <CardBody className="p-2">
                                  {vehicleSearchResults.map(vehicle => (
                                    <div
                                      key={vehicle.id}
                                      className="p-2 border-bottom cursor-pointer hover-bg"
                                      onClick={() =>
                                        handleVehicleSelect(vehicle)
                                      }
                                    >
                                      <div className="fw-bold text-primary">
                                        {getVehiclePreviewLabel(vehicle) ||
                                          "Unknown Vehicle"}
                                      </div>
                                      <div className="small text-muted">
                                        Stock: {vehicle?.stock_id || "-"}
                                        {" | "}Chassis:{" "}
                                        {vehicle?.full_chassis || "-"}
                                        {" | "}Engine:{" "}
                                        {vehicle?.full_engine || "-"}
                                        {" | "}Color:{" "}
                                        {getDisplayText(
                                          vehicle?.exterior_color
                                        ) || "-"}
                                      </div>
                                    </div>
                                  ))}
                                </CardBody>
                              </Card>
                            )}

                          {showVehicleSuggestions &&
                            vehicleSearchTerm.length >= 2 &&
                            !vehicleSearching &&
                            !vehicleSearchResults?.length && (
                              <Card className="position-absolute w-100 mt-1">
                                <CardBody className="p-3 text-center text-muted">
                                  <i className="bx bx-car fs-4 mb-2 d-block"></i>
                                  No vehicles found
                                  <div className="small mt-1">
                                    You can still enter chassis/engine below or
                                    keep only a description
                                  </div>
                                </CardBody>
                              </Card>
                            )}
                        </div>
                      )}
                    </Col>

                    {selectedVehicleItem && (
                      <Col md={12}>
                        <div className="selection-preview-card vehicle-preview-card">
                          <div className="selection-preview-icon">
                            <i className="bx bx-car"></i>
                          </div>
                          <div className="w-100">
                            <h6 className="mb-1">Vehicle Details</h6>
                            <div className="text-muted small">
                              Stock: {selectedVehicleItem?.stock_id || "-"}
                              {"  "}Chassis:{" "}
                              {selectedVehicleItem?.full_chassis || "-"}
                            </div>
                            {getVehiclePreviewLabel(selectedVehicleItem) && (
                              <div className="small mt-1">
                                {getVehiclePreviewLabel(selectedVehicleItem)}
                              </div>
                            )}
                            <div className="small mt-2">
                              Color:{" "}
                              {getDisplayText(
                                selectedVehicleItem?.exterior_color
                              ) || "-"}
                              {"  "}Year:{" "}
                              {getDisplayText(selectedVehicleItem?.year) ||
                                "-"}
                              {"  "}Engine:{" "}
                              {selectedVehicleItem?.full_engine || "-"}
                            </div>
                          </div>
                        </div>
                      </Col>
                    )}

                    <Col md={12}>
                      <div className="editor-panel">
                        <Row className="g-3">
                          <Col md={12}>
                            <Label className="form-label">Name</Label>
                            <Input
                              name="pdf_content.vehicle_info.name"
                              value={vehicleInfoValues.name || ""}
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col md={6}>
                            <Label className="form-label">Full Chassis</Label>
                            <Input
                              name="pdf_content.vehicle_info.full_chassis"
                              placeholder="e.g. NZE141-1234567"
                              value={vehicleInfoValues.full_chassis || ""}
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col md={6}>
                            <Label className="form-label">Full Engine</Label>
                            <Input
                              name="pdf_content.vehicle_info.full_engine"
                              placeholder="e.g. 1NZ-1234567"
                              value={vehicleInfoValues.full_engine || ""}
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col md={4}>
                            <Label className="form-label">Year/Model</Label>
                            <Input
                              name="pdf_content.vehicle_info.year_model"
                              value={vehicleInfoValues.year_model || ""}
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col md={4}>
                            <Label className="form-label">Color</Label>
                            <Input
                              name="pdf_content.vehicle_info.color"
                              value={vehicleInfoValues.color || ""}
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col md={4}>
                            <Label className="form-label">CC</Label>
                            <Input
                              name="pdf_content.vehicle_info.cc"
                              value={vehicleInfoValues.cc || ""}
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col md={12}>
                            <Label className="form-label">Fuel</Label>
                            <Input
                              name="pdf_content.vehicle_info.fuel"
                              value={vehicleInfoValues.fuel || ""}
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col md={12}>
                            <Label className="form-label">
                              Vehicle Description
                            </Label>
                            <Input
                              type="textarea"
                              rows={3}
                              name="vehicle_description"
                              placeholder="e.g. Toyota Axio Hybrid, pearl white, 2021, non-fresh"
                              value={formik.values.vehicle_description}
                              onChange={formik.handleChange}
                              invalid={
                                !!(
                                  formik.touched.vehicle_description &&
                                  formik.errors.vehicle_description
                                )
                              }
                            />
                            <FormFeedback>
                              {formik.errors.vehicle_description}
                            </FormFeedback>
                          </Col>
                          {!hasVehicleTarget && (
                            <Col md={12}>
                              <div className="text-muted small">
                                No chassis/engine yet — the booking will be
                                saved with the description only, and the
                                vehicle can be added later once purchased.
                              </div>
                            </Col>
                          )}
                        </Row>
                      </div>
                    </Col>

                    <Col md={12}>
                      <div className="section-header">
                        <h6>PAYMENT SUMMARY</h6>
                        <hr />
                      </div>
                    </Col>

                    <Col md={4}>
                      <Label className="form-label">Price</Label>
                      <Input
                        type="number"
                        name="car_price"
                        value={formik.values.car_price}
                        onChange={formik.handleChange}
                        invalid={
                          !!(
                            formik.touched.car_price && formik.errors.car_price
                          )
                        }
                      />
                      <FormFeedback>{formik.errors.car_price}</FormFeedback>
                    </Col>

                    <Col md={4}>
                      <Label className="form-label">Registration Cost</Label>
                      <Input
                        type="number"
                        name="registration_cost"
                        value={formik.values.registration_cost}
                        onChange={formik.handleChange}
                        invalid={
                          !!(
                            formik.touched.registration_cost &&
                            formik.errors.registration_cost
                          )
                        }
                      />
                      <FormFeedback>
                        {formik.errors.registration_cost}
                      </FormFeedback>
                    </Col>

                    <Col md={4}>
                      <div className="total-section h-100">
                        {summaryInfo.length > 0 ? (
                          summaryInfo.map(item => (
                            <div
                              key={item.label}
                              className={`d-flex justify-content-between gap-3 ${
                                item.strong
                                  ? "fw-bold fs-6 border-top pt-2 mt-2"
                                  : "mb-2"
                              }`}
                            >
                              <span>{item.label}</span>
                              <span>{formatAmount(item.value)}</span>
                            </div>
                          ))
                        ) : (
                          <div className="text-muted">
                            Enter price to build the payment summary.
                          </div>
                        )}
                      </div>
                    </Col>

                    <Col md={12}>
                      <div className="section-header">
                        <h6>NOTES</h6>
                        <hr />
                      </div>
                    </Col>

                    <Col md={12}>
                      <Input
                        type="textarea"
                        rows={3}
                        name="notes"
                        placeholder="Additional notes or comments..."
                        value={formik.values.notes}
                        onChange={formik.handleChange}
                        invalid={!!(formik.touched.notes && formik.errors.notes)}
                      />
                      <FormFeedback>{formik.errors.notes}</FormFeedback>
                    </Col>

                    <Col md={12}>
                      <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button
                          type="button"
                          color="light"
                          onClick={() => navigate(fromPath)}
                          disabled={isSaving}
                        >
                          Cancel
                        </Button>
                        <Button type="submit" color="primary" disabled={isSaving}>
                          {isSaving
                            ? "Saving..."
                            : isEdit
                              ? "Update Pre-Order"
                              : "Create Pre-Order"}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </Form>
          )}
        </CardBody>
      </Card>
    </PageWrapper>
  )
}

export default PreOrderAddEditPage
